import { useState, useCallback, useMemo } from 'react';
import { AnyService, ServiceStatus } from '../types/service.type';

interface UseServiceActionsProps {
  service: AnyService;
  onSuccess?: (action: string, service: AnyService) => void;
  onError?: (error: Error, action: string) => void;
}

interface UseServiceActionsReturn {
  // Estado
  isLoading: boolean;
  currentAction: string | null;
  error: Error | null;
  
  // Acciones
  startService: () => Promise<void>;
  stopService: () => Promise<void>;
  restartService: () => Promise<void>;
  deleteService: () => Promise<void>;
  
  // Validaciones
  canStart: boolean;
  canStop: boolean;
  canRestart: boolean;
  canDelete: boolean;
  canVisit: boolean;
  
  // URLs
  visitUrl: string | null;
  adminUrl: string | null;
}

export const useServiceActions = ({
  service,
  onSuccess,
  onError,
}: UseServiceActionsProps): UseServiceActionsReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Simulación de llamada a API
  const simulateApiCall = async (action: string, newStatus: ServiceStatus) => {
    setIsLoading(true);
    setCurrentAction(action);
    setError(null);

    try {
      // Simular delay de red
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Simular posible error (10% de probabilidad)
      if (Math.random() < 0.1) {
        throw new Error(`Failed to ${action} service`);
      }

      // Actualizar el estado del servicio (en producción, esto vendría del backend)
      service.status = newStatus;
      service.lastActionAt = new Date();

      onSuccess?.(action, service);
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      setError(errorObj);
      onError?.(errorObj, action);
      throw errorObj;
    } finally {
      setIsLoading(false);
      setCurrentAction(null);
    }
  };

  // Validaciones (memoizadas)
  const canStart = useMemo(() => {
    return service.status === 'stopped' || service.status === 'error';
  }, [service.status]);

  const canStop = useMemo(() => {
    return service.status === 'running' || service.status === 'error';
  }, [service.status]);

  const canRestart = useMemo(() => {
    return service.status === 'running' || service.status === 'error';
  }, [service.status]);

  const canDelete = useMemo(() => {
    return (
      service.status !== 'deploying' &&
      service.status !== 'stopping' &&
      service.status !== 'starting' &&
      service.status !== 'restarting'
    );
  }, [service.status]);

  // Acciones
  const startService = useCallback(async () => {
    if (!canStart) return;
    await simulateApiCall('start', 'starting');
    // Simular que después de 3 segundos pasa a "running"
    setTimeout(() => {
      service.status = 'running';
    }, 3000);
  }, [service, canStart]);

  const stopService = useCallback(async () => {
    if (!canStop) return;
    await simulateApiCall('stop', 'stopping');
    setTimeout(() => {
      service.status = 'stopped';
    }, 3000);
  }, [service, canStop]);

  const restartService = useCallback(async () => {
    if (!canRestart) return;
    await simulateApiCall('restart', 'restarting');
    setTimeout(() => {
      service.status = 'running';
    }, 4000);
  }, [service, canRestart]);

  const deleteService = useCallback(async () => {
    if (!canDelete) return;
    await simulateApiCall('delete', 'terminated');
  }, [service, canDelete]);

  const canVisit = useMemo(() => {
    return (
      service.status === 'running' &&
      service.health !== 'unhealthy' &&
      !!service.primaryUrl
    );
  }, [service.status, service.health, service.primaryUrl]);

  // URLs
  const visitUrl = useMemo(() => {
    return canVisit ? service.primaryUrl || null : null;
  }, [canVisit, service.primaryUrl]);

  const adminUrl = useMemo(() => {
    return service.status === 'running' ? service.adminUrl || null : null;
  }, [service.status, service.adminUrl]);

  return {
    isLoading,
    currentAction,
    error,
    startService,
    stopService,
    restartService,
    deleteService,
    canStart,
    canStop,
    canRestart,
    canDelete,
    canVisit,
    visitUrl,
    adminUrl,
  };
};